import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';
import { getIncentives, updateIncentive, uploadIncentiveData } from '../services/api';

const { FiDatabase, FiUpload, FiEdit2, FiSave, FiX } = FiIcons;

const Admin = () => {
  const [incentives, setIncentives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [uploadingCsv, setUploadingCsv] = useState(false);

  useEffect(() => {
    fetchIncentives();
  }, []);

  const fetchIncentives = async () => {
    try {
      const data = await getIncentives();
      setIncentives(data);
    } catch (error) {
      toast.error('Failed to load incentives');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (incentive) => {
    setEditingId(incentive.id);
    setEditData({
      processing_days_estimate: incentive.processing_days_estimate || '',
      reliability_score_0_1: incentive.reliability_score_0_1 || '',
      flexibility_score_0_1: incentive.flexibility_score_0_1 || '',
      green_bonus_flag: incentive.green_bonus_flag || false,
      vfx_bonus_flag: incentive.vfx_bonus_flag || false,
      genre_exclusions: incentive.genre_exclusions || '',
      application_window_notes: incentive.application_window_notes || ''
    });
  };

  const handleSave = async (id) => {
    try {
      await updateIncentive(id, editData);
      toast.success('Incentive updated successfully');
      setEditingId(null);
      fetchIncentives();
    } catch (error) {
      toast.error('Failed to update incentive');
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadingCsv(true);
    try {
      // Mock CSV processing
      const text = await file.text();
      const rows = text.split('\n').filter(row => row.trim());
      
      await uploadIncentiveData(rows);
      toast.success(`Successfully uploaded ${rows.length} rows`);
      fetchIncentives();
    } catch (error) {
      toast.error('Failed to upload CSV');
    } finally {
      setUploadingCsv(false);
    }
  };

  const qualitativeFields = [
    { key: 'processing_days_estimate', label: 'Processing Days', type: 'number' },
    { key: 'reliability_score_0_1', label: 'Reliability Score (0-1)', type: 'number', step: '0.1', min: '0', max: '1' },
    { key: 'flexibility_score_0_1', label: 'Flexibility Score (0-1)', type: 'number', step: '0.1', min: '0', max: '1' },
    { key: 'green_bonus_flag', label: 'Green Bonus', type: 'checkbox' },
    { key: 'vfx_bonus_flag', label: 'VFX Bonus', type: 'checkbox' },
    { key: 'genre_exclusions', label: 'Genre Exclusions', type: 'text' },
    { key: 'application_window_notes', label: 'Application Notes', type: 'textarea' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - NuPitch</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container-responsive py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-8"
          >
            <div className="flex items-center justify-between">
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              
              <div className="flex items-center space-x-4">
                <label className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors cursor-pointer">
                  <SafeIcon icon={FiUpload} className="w-4 h-4 inline mr-2" />
                  Upload CSV
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvUpload}
                    className="hidden"
                    disabled={uploadingCsv}
                  />
                </label>
                
                {uploadingCsv && (
                  <div className="flex items-center space-x-2 text-gray-600">
                    <div className="spinner w-4 h-4"></div>
                    <span>Uploading...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiDatabase} className="w-6 h-6 text-primary-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Total Incentives</p>
                    <p className="text-2xl font-bold text-gray-900">{incentives.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiEdit2} className="w-6 h-6 text-success-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Curated</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {incentives.filter(i => i.reliability_score_0_1).length}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-warning-100 rounded-full flex items-center justify-center">
                    <SafeIcon icon={FiX} className="w-6 h-6 text-warning-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500">Needs Curation</p>
                    <p className="text-2xl font-bold text-gray-900">
                      {incentives.filter(i => !i.reliability_score_0_1).length}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Incentives Table */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-xl font-bold text-gray-900">Incentive Database</h2>
                <p className="text-gray-600">Manage qualitative fields for incentive scoring</p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Jurisdiction
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Incentive
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rate
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {incentives.map((incentive) => (
                      <tr key={incentive.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {incentive.jurisdiction_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {incentive.region_group}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">
                            {incentive.incentive_name}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                            {incentive.incentive_type}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {incentive.base_rate_percent}%
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {incentive.reliability_score_0_1 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-800">
                              Curated
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-800">
                              Needs Curation
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {editingId === incentive.id ? (
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleSave(incentive.id)}
                                className="text-success-600 hover:text-success-900"
                              >
                                <SafeIcon icon={FiSave} className="w-4 h-4" />
                              </button>
                              <button
                                onClick={handleCancel}
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <SafeIcon icon={FiX} className="w-4 h-4" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleEdit(incentive)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Edit Modal */}
            {editingId && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">
                    Edit Qualitative Fields
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {qualitativeFields.map((field) => (
                      <div key={field.key} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {field.label}
                        </label>
                        {field.type === 'checkbox' ? (
                          <input
                            type="checkbox"
                            checked={editData[field.key] || false}
                            onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.checked }))}
                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                          />
                        ) : field.type === 'textarea' ? (
                          <textarea
                            value={editData[field.key] || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        ) : (
                          <input
                            type={field.type}
                            value={editData[field.key] || ''}
                            onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                            step={field.step}
                            min={field.min}
                            max={field.max}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex justify-end space-x-3 mt-6">
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSave(editingId)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default Admin;